import { apiCall } from '../../../../_services/apiCall'; // Adjust path as needed
import config from '../../../../config/config.json'; // Adjust path as needed


/* ==================== IOT Meters CRUD ==================== */

/**
 * Get all meters
 */
export const getMeters = async () => {
  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters`,
      {},
      {},
      "GET"
    );
    
    return isSuccess ? data : [];
  } catch (error) {
    console.error('Error fetching meters:', error);
    throw error;
  }
};

/**
 * Get single meter with KPI mappings
 */
export const getMeter = async (meterId) => {
  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/${meterId}`,
      {},
      {},
      "GET"
    );
    
    return isSuccess ? data : null;
  } catch (error) {
    console.error('Error fetching meter:', error);
    throw error;
  }
};

/**
 * Create new meter
 */
export const createMeter = async (meterData) => {
  try {
    const { isSuccess, data, message } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters`,
      meterData,
      {},
      "POST"
    );
    
    if (isSuccess) return data;
    throw new Error(message || 'Failed to create meter');
  } catch (error) {
    console.error('Error creating meter:', error);
    throw error;
  }
};

/**
 * Update meter
 */
export const updateMeter = async (meterId, meterData) => {
  try {
    const { isSuccess, data, message } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/${meterId}`,
      meterData,
      {},
      "PUT"
    );
    
    if (isSuccess) return data;
    throw new Error(message || 'Failed to update meter');
  } catch (error) {
    console.error('Error updating meter:', error);
    throw error;
  }
};

/**
 * Delete meter
 */
export const deleteMeter = async (meterId) => {
  try {
    const { isSuccess, data, message } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/${meterId}`,
      {},
      {},
      "DELETE"
    );
    
    if (isSuccess) return data;
    throw new Error(message || 'Failed to delete meter');
  } catch (error) {
    console.error('Error deleting meter:', error);
    throw error;
  }
};

/* ==================== KPI Management ==================== */

/**
 * Get all KPIs (optionally filtered by type)
 * @param {object} params - { kpiType: 'INTERNAL' | 'EXTERNAL' | undefined }
 */
export const getKPIs = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.kpiType) {
      queryParams.append('kpiType', params.kpiType);
    }
    
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/kpis`, 
      {}, 
      {kpiType: params.kpiType}, 
      "GET"
    );

    return isSuccess ? data : [];
  } catch (error) {
    console.error('Error fetching KPIs:', error);
    throw error;
  }
};

/**
 * Get single KPI by ID
 */
export const getKPI = async (kpiId) => {
  try {
    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/kpis/${kpiId}`,
      {},
      {},
      "GET"
    );
    
    return isSuccess ? data : null;
  } catch (error) {
    console.error('Error fetching KPI:', error);
    throw error;
  }
};

/**
 * Create new KPI
 */
export const createKPI = async (kpiData) => {
  try {
    const { isSuccess, data, message } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/kpis/`,
      {},
      kpiData,
      "POST"
    );
    
    if (isSuccess) return data;
    throw new Error(message || 'Failed to create KPI');
  } catch (error) {
    console.error('Error creating KPI:', error);
    throw error;
  }
};

/**
 * Delete KPI
 */
export const deleteKPI = async (kpiId) => {
  try {
    const { isSuccess, data, message } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}iot-meters/kpis/${kpiId}`,
      {},
      {},
      "DELETE"
    );
    
    if (isSuccess) return data;
    throw new Error(message || 'Failed to delete KPI');
  } catch (error) {
    console.error('Error deleting KPI:', error);
    throw error;
  }
};

/* ==================== Analytics / Graph Data ==================== */

/**
 * Generic function to fetch graph data with custom widget config
 * Used for all meter visualizations
 */
export const getGraphData = async (graphConfig) => {
  try {

    const { isSuccess, data } = await apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}bi-graph/preview`,
      {},
      graphConfig,
      "POST",
      false,
      false
    );
    
    if (isSuccess && data?.data) {
      return {
        data: data.data,
        metadata: data.metadata
      };
    }
    
    return { data: [], metadata: {} };
  } catch (error) {
    console.error('Error fetching graph data:', error);
    throw error;
  }
};

/**
 * Helper to build widget config for bi-graph API
 */
export const buildWidgetConfig = ({
  meterId,
  kpiId,
  module,
  category,
  subCategory,
  kpi,
  kpiType,
  measurementType,
  financialYear,
  startDateTime,
  endDateTime,
  aggregationPeriodValue = 1,
  aggregationPeriodUnit = 'days',
  locationId = '1',
  title = '',
  chartType = 'line',
  xAxisField = 'displayPeriods',
  stackByField = null
}) => {
  return {
    title,
    dataSourceType: 'IOT_METERS',
    description: '',
    chartType,
    xAxisField,
    stackByField,
    enableStacking: false,
    representationType: 'Graph',
    widgetConfig: {
      module: [module],
      kpiType: kpiType,
      measurementType: measurementType,
      filters: {
        financial_year: [financialYear],
        location_id: locationId ? [locationId] : [],
        sublocation_id: [],
        category: category ? [category] : [],
        sub_category: subCategory ? [subCategory] : [],
        kpi: kpi ? [kpi] : [],
        displayPeriods: [],
        startDateTime,
        endDateTime,
        aggregationPeriodValue,
        aggregationPeriodUnit,
        meter_id: meterId ? (Array.isArray(meterId) ? meterId : [meterId]) : [],
      },
      group_by: stackByField ? [xAxisField, stackByField] : [xAxisField],
      aggregate: {
        sum: 'value'
      },
      sort: {
        field: xAxisField,
        order: 'asc'
      },
      aggregateSubLocations: true
    }
  };
};